import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  IconButton,
  useDisclosure,
  Skeleton,
  Text,
  HStack,
  Link,
  Tooltip,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Switch,
  FormControl,
  FormLabel,
  Badge,
  VStack,
} from '@chakra-ui/react';
import { Eye, ExternalLink, Trash2, RefreshCw, Settings, Youtube } from 'lucide-react';
import { fetchPendingDrafts, supabase } from '../lib/supabase';
import type { Draft } from '../lib/supabase';
import DraftPreview from './DraftPreview';

interface DraftsListProps {
  siteId?: string;
}

export default function DraftsList({ siteId }: DraftsListProps) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const loadDrafts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchPendingDrafts(siteId);
      setDrafts(data);
    } catch (error) {
      console.error('Error loading drafts:', error);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  useEffect(() => {
    const channelName = siteId ? `drafts-realtime-${siteId}` : 'drafts-realtime';
    const filter = siteId
      ? { event: 'INSERT' as const, schema: 'public', table: 'blog_posts', filter: `site_id=eq.${siteId}` }
      : { event: 'INSERT' as const, schema: 'public', table: 'blog_posts' };

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        filter,
        (payload) => {
          const row = payload.new as { status?: string; title?: string };
          if (row.status === 'pending_review') {
            loadDrafts();
            toast({
              title: 'New draft ready for review',
              description: row.title ? `"${row.title}"` : 'A new article has been generated.',
              status: 'info',
              duration: 6000,
              isClosable: true,
              position: 'top-right',
            });
          }
        }
      )
      .on(
        'postgres_changes',
        siteId
          ? { event: 'UPDATE' as const, schema: 'public', table: 'blog_posts', filter: `site_id=eq.${siteId}` }
          : { event: 'UPDATE' as const, schema: 'public', table: 'blog_posts' },
        () => {
          loadDrafts();
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE' as const, schema: 'public', table: 'blog_posts' },
        () => {
          loadDrafts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [siteId, loadDrafts, toast]);

  function handlePreview(draft: Draft) {
    setSelectedDraft(draft);
    onOpen();
  }

  function handleDraftUpdated() {
    loadDrafts();
    onClose();
  }

  function confirmDelete(draft: Draft) {
    setDeleteTarget(draft);
    onDeleteOpen();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', deleteTarget.id);
      
      if (error) throw error;
      
      toast({
        title: 'Draft deleted',
        description: `"${deleteTarget.title}" has been removed.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      loadDrafts();
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast({
        title: 'Delete failed',
        description: 'Could not delete the draft. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
      onDeleteClose();
    }
  }

  function formatRelativeTime(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function formatFullDateTime(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (loading) {
    return (
      <Box>
        <Skeleton height="24px" mb={3} width="150px" borderRadius="md" />
        <Skeleton height="250px" borderRadius="lg" />
      </Box>
    );
  }

  if (drafts.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
          No Pending Drafts
        </Text>
        <Text color="gray.500" fontSize="xs">All drafts have been reviewed!</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack mb={3} justify="space-between" align="center">
        <HStack spacing={2}>
          <Heading size="xs" color="gray.700" fontWeight="semibold" fontSize="13px">
            Pending Drafts
          </Heading>
          <Badge 
            bg="brand.100" 
            color="brand.700" 
            fontSize="10px" 
            px={1.5} 
            py={0.5} 
            borderRadius="full"
            fontWeight="medium"
          >
            {drafts.length}
          </Badge>
        </HStack>
        
        <HStack spacing={2}>
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel htmlFor="edit-mode" mb="0" fontSize="10px" color="gray.500" mr={1.5}>
              <Settings size={12} style={{ display: 'inline', marginRight: 3 }} />
              Edit
            </FormLabel>
            <Switch
              id="edit-mode"
              size="sm"
              colorScheme="orange"
              isChecked={editMode}
              onChange={(e) => setEditMode(e.target.checked)}
            />
          </FormControl>
          
          <Tooltip label="Refresh list" placement="top" fontSize="xs">
            <IconButton
              aria-label="Refresh"
              icon={<RefreshCw size={14} />}
              size="xs"
              variant="ghost"
              color="gray.500"
              onClick={loadDrafts}
              _hover={{ color: 'brand.600', bg: 'brand.50' }}
            />
          </Tooltip>
        </HStack>
      </HStack>

      <Box
        bg="white"
        borderRadius="md"
        boxShadow="sm"
        borderWidth={1}
        borderColor="gray.200"
        overflow="hidden"
      >
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th
                color="gray.500"
                textTransform="uppercase"
                fontSize="9px"
                fontWeight="semibold"
                letterSpacing="wider"
                py={2}
              >
                Title
              </Th>
              <Th
                color="gray.500"
                textTransform="uppercase"
                fontSize="9px"
                fontWeight="semibold"
                letterSpacing="wider"
                py={2}
                w="60px"
              >
                Score
              </Th>
              <Th
                color="gray.500"
                textTransform="uppercase"
                fontSize="9px"
                fontWeight="semibold"
                letterSpacing="wider"
                py={2}
                w="140px"
              >
                Source
              </Th>
              <Th
                color="gray.500"
                textTransform="uppercase"
                fontSize="9px"
                fontWeight="semibold"
                letterSpacing="wider"
                py={2}
                w="130px"
              >
                Created
              </Th>
              <Th
                color="gray.500"
                textTransform="uppercase"
                fontSize="9px"
                fontWeight="semibold"
                letterSpacing="wider"
                py={2}
                w="90px"
                textAlign="right"
              >
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {drafts.map((draft) => (
              <Tr
                key={draft.id}
                borderBottomWidth={1}
                borderColor="gray.100"
                _last={{ borderBottom: 'none' }}
                _hover={{ bg: 'brand.50', transition: 'background 0.15s' }}
              >
                <Td maxW="280px" py={2}>
                  <Text 
                    isTruncated 
                    fontWeight="medium" 
                    color="gray.800" 
                    fontSize="xs"
                    title={draft.title}
                  >
                    {draft.title}
                  </Text>
                </Td>
                <Td py={2}>
                  <VStack spacing={1} align="start">
                    {draft.quality_score != null ? (
                      <Badge
                        colorScheme={draft.quality_score >= 80 ? 'green' : draft.quality_score >= 60 ? 'yellow' : 'red'}
                        fontSize="10px"
                        borderRadius="full"
                        px={1.5}
                      >
                        {draft.quality_score}
                      </Badge>
                    ) : (
                      <Text fontSize="10px" color="gray.400">—</Text>
                    )}
                    {draft.regenerating_image != null && (
                      <Badge colorScheme="blue" fontSize="9px" borderRadius="full" px={1.5}>
                        Regen img {draft.regenerating_image}
                      </Badge>
                    )}
                    {draft.image_errors && (
                      <Tooltip label={draft.image_errors.error} fontSize="xs">
                        <Badge colorScheme="orange" fontSize="9px" borderRadius="full" px={1.5}>
                          {draft.image_errors.failed_count} img failed
                        </Badge>
                      </Tooltip>
                    )}
                  </VStack>
                </Td>
                <Td py={2}>
                  {draft.video_id ? (
                    <Link
                      href={`https://youtube.com/watch?v=${draft.video_id}`}
                      isExternal
                      display="flex"
                      alignItems="center"
                      gap={1}
                      _hover={{ textDecoration: 'none' }}
                    >
                      <HStack spacing={1}>
                        <Youtube size={12} color="#DC2626" />
                        <Text
                          fontSize="10px"
                          color="gray.600"
                          fontFamily="mono"
                          _hover={{ color: 'brand.600' }}
                        >
                          {draft.video_id}
                        </Text>
                        <ExternalLink size={10} color="#9CA3AF" />
                      </HStack>
                    </Link>
                  ) : (
                    <Text fontSize="10px" color="gray.400">—</Text>
                  )}
                </Td>
                <Td py={2}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="10px" color="gray.600" fontWeight="medium">
                      {formatRelativeTime(draft.created_at)}
                    </Text>
                    <Text fontSize="9px" color="gray.400">
                      {formatFullDateTime(draft.created_at)}
                    </Text>
                  </VStack>
                </Td>
                <Td py={2}>
                  <HStack spacing={1} justify="flex-end">
                    {editMode && (
                      <Tooltip label="Delete draft" placement="top" fontSize="xs">
                        <IconButton
                          aria-label="Delete"
                          icon={<Trash2 size={12} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => confirmDelete(draft)}
                          _hover={{ bg: 'red.50' }}
                        />
                      </Tooltip>
                    )}
                    <Button
                      size="xs"
                      bg="brand.400"
                      color="gray.900"
                      fontWeight="medium"
                      fontSize="10px"
                      h="22px"
                      px={2}
                      onClick={() => handlePreview(draft)}
                      leftIcon={<Eye size={12} />}
                      _hover={{
                        bg: 'brand.500',
                        transform: 'translateY(-1px)',
                        shadow: 'sm',
                      }}
                      _active={{
                        transform: 'translateY(0)',
                      }}
                      transition="all 0.15s"
                    >
                      Review
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {selectedDraft && (
        <DraftPreview
          key={selectedDraft.id}
          isOpen={isOpen}
          onClose={onClose}
          draft={selectedDraft}
          onDraftUpdated={handleDraftUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="lg" maxW="380px">
            <AlertDialogHeader fontSize="md" fontWeight="semibold" pb={1}>
              Delete Draft
            </AlertDialogHeader>

            <AlertDialogBody color="gray.600" fontSize="xs">
              Are you sure you want to delete{' '}
              <Text as="span" fontWeight="medium" color="gray.800">
                "{deleteTarget?.title}"
              </Text>
              ? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter gap={2} pt={3}>
              <Button 
                ref={cancelRef} 
                onClick={onDeleteClose} 
                size="xs"
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                isLoading={deleting}
                loadingText="Deleting..."
                size="xs"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
